export interface EmbedContext {
  /** Whether the app is running inside a frame rather than as the top document. */
  isIframed: boolean;
  /** Comma-joined ancestor origins, outermost last. Empty when there are none. */
  embedAncestorOrigins: string;
  /** How many ancestor origins we could read. */
  embedAncestorCount: number;
}

const NOT_EMBEDDED: EmbedContext = {
  isIframed: false,
  embedAncestorOrigins: '',
  embedAncestorCount: 0,
};

function readAncestorOrigins(): string[] {
  const { ancestorOrigins } = window.location;
  if (ancestorOrigins?.length) return Array.from(ancestorOrigins);

  // Firefox has no ancestorOrigins and the referrer only reaches one level up,
  // so a deeper chain reports as a single ancestor rather than not at all.
  if (!document.referrer) return [];
  try {
    return [new URL(document.referrer).origin];
  } catch {
    return [];
  }
}

/**
 * Describes how a partner has embedded the flow.
 *
 * Partners either iframe us into their own site or point a mobile webview
 * straight at our URL, and the two fail differently: a webview's host app can
 * refuse the third-party subframes TokenEx needs, which the iframe path does
 * not. A top-level document inside a webview and a top-level document in a
 * real browser are indistinguishable here — the user agent separates those —
 * but an embedded page and a top-level one are not, and that is the half this
 * answers.
 *
 * Reports rather than validates, and never throws: it runs during RUM init,
 * before anything is in place to catch it. Compare `useTxOrigin`, which does
 * validate the same chain against an allowlist and throws on a miss.
 */
export function readEmbedContext(): EmbedContext {
  if (typeof window === 'undefined') return NOT_EMBEDDED;

  try {
    if (window.self === window.top) return NOT_EMBEDDED;

    const ancestors = readAncestorOrigins();
    return {
      isIframed: true,
      embedAncestorOrigins: ancestors.join(','),
      embedAncestorCount: ancestors.length,
    };
  } catch {
    // A cross-origin window.top comparison is allowed, but a hostile or
    // unusual container can still make this throw. Being embedded is the
    // safer assumption: only an embedded page can fail this way.
    return { isIframed: true, embedAncestorOrigins: '', embedAncestorCount: 0 };
  }
}
